import {
  VTextField,
  VTextarea,
  VSelect,
  VAutocomplete,
  VSwitch,
} from 'vuetify/components';

import DynamicJsonField from '@/components/form/DynamicJsonField.vue'
import ProductSelector from '@/components/form/ProductSelector.vue'
import FileUploader from '@/components/form/FileUploader.vue';
import DatePickerInput from '@/components/form/DatePickerInput.vue';
import GroupedFieldsExpansionPanel from '@/components/form/GroupedFieldsExpansionPanel.vue'

import type { FormField } from '@/types/form';
import type { CardField } from '@/types/cards';

import SaleDetail from '@/components/modules/sales/Detail.vue'

function getHostnameSafe(): string {
  // Navegador
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  // SSR / Node (si lo necesitas, puedes leer una var de entorno)
  return process.env.VITE_PUBLIC_HOST ?? process.env.SSR_HOST ?? '';
}

// Usa comparación estricta; ajusta si necesitas subdominios.
const IS_DOMINIOMEDIOS = getHostnameSafe() === 'crm.dominiomedios.com';

export const CREATE_STEPS: Array<{ title?: string; description?: string; fields: FormField[] }> = [
  {
    title: 'Datos generales',
    fields: [
      /*{ value: 'user_id', 
        permission_state:'hidden',
        permission:'activities.assign',
        label: 'Vendedor', 
        component: VAutocomplete, 
        required: false, 
        col: 6, 
        fetchOptions: 'searchUsers', 
        searchValue: 'name', 
        itemText: (item: any) => [item?.name, item?.last_name].filter(Boolean).join(' ') || '', 
        itemValue: 'id',
        fetchParams: () => ({
          'filter[is_active]': 1
        }),
      },*/
      { value: 'agency', 
        label: 'Agencia', 
        component: VAutocomplete, 
        searchValue: 'name', 
        required: true, 
        col: 6, 
        mdcol:12, 
        fetchOptions: 'searchClients',
        fetchParams: () => ({ 'filter[tag]': 'Agencia' }),
        itemText: (item: any) => item?.attributes?.commercial_name || item?.attributes?.legal_name || item?.attributes?.name || '', 
        itemValue: (item: any) => item,
        transform: (value: any) => ({
          agency_id: value?.id ?? value,
        })
      },
      { value: 'brand', 
        label: 'Anunciante (Marca)', 
        component: VAutocomplete, 
        searchValue: 'name', 
        required: false, 
        col: 6, 
        mdcol:12, 
        fetchOptions: 'searchClients',
        fetchParams: () => ({ 'filter[tag]': 'Marca' }),
        itemText: (item: any) => item?.attributes?.commercial_name || item?.attributes?.legal_name || item?.attributes?.name || '', 
        itemValue: (item: any) => item,
        parse: (_: any, item: any) => item?.account || null,
        transform: (value: any) => ({
          account_id: value?.id ?? value,
        })
      },
      {
        value: 'contact_finance',
        label: 'Contacto Financiero',
        component: VAutocomplete,
        searchValue: 'name',
        required: false,
        col: 6,
        fetchOptions: 'searchContacts',
        itemText: (item: any) => {
          if (!item) return '';
          const name = item?.name || '';
          const last = item?.last || '';
          return `${name} ${last}`;
        },
        itemValue: (item: any) => item,
        dependsOn: 'agency',
        dependsBehavior: 'hidden',
        dependsCondition: { value: null, condition: 'diferent' },
        fetchParams: (form: any) => ({
          'filter[client_id]': form?.agency?.id
        }),
        transform: (value: any) => ({
          contact_finance_id: value?.id,
        })
      },
      {
        value: 'contact_campaign',
        label: 'Contacto de Campaña',
        component: VAutocomplete,
        searchValue: 'name',
        required: false,
        col: 6,
        fetchOptions: 'searchContacts',
        itemText: (item: any) => {
          if (!item) return '';
          const name = item?.name || '';
          const last = item?.last || '';
          return `${name} ${last}`;
        },
        itemValue: (item: any) => item,
        dependsOn: 'agency',
        dependsBehavior: 'hidden',
        dependsCondition: { value: null, condition: 'diferent' },
        fetchParams: (form: any) => ({
          'filter[client_id]': form?.agency?.id
        }),
        transform: (value: any) => ({
          contact_campaign_id: value?.id,
        })
      },
      { value: 'campaign', label: 'Campaña', component: VTextField, required: false, col: 6, mdcol:12 },
      { value: 'period', label: 'Periodo', component: VTextField, required: false, col: 6, mdcol:12 },
      
      { value: 'influencer_id', 
        permission_state:'hidden',
        label: 'Influencer', 
        component: VAutocomplete, 
        required: false, 
        col: 6, 
        fetchOptions: 'searchUsers', 
        searchValue: 'name', 
        itemText: (item: any) => [item?.name, item?.last_name].filter(Boolean).join(' ') || '', 
        itemValue: 'id',
        fetchParams: () => ({
          'filter[is_active]': 1
        }),
      },
      {
        value: 'sale_items',
        label: 'Servicios',
        isJsonArray: true,
        component: DynamicJsonField,
        col: 12,
        transform: (items: any[]) =>
          items.map(item => ({
            quantity: Number(item.quantity) || 1,
            sold_item_type: item.productable_type || 'Product',
            sold_item_id: item.productable_type === 'Product' ? item.item_id : item.productable_id,
            unit_price: item.unit_price,
          })
        ),
        children: [
          {
            value: 'quantity',
            label: 'Cantidad',
            component: VTextField,
            type: 'number',
            required: true,
            col: 2,
          },
          {
            value: 'product_selector',
            label: 'Servicio',
            component: ProductSelector,
            col: 7,
            isCustom: true,
            required: true,
            props: (row: Record<string, any>, form: Record<string, any>) => ({
              subject: form?.client,
            }),
          },
          {
            value: 'date',
            label: 'Fecha',
            component:DatePickerInput,
            type: 'number',
            required: true,
            col: 3,
          },
        ],
        summary: {
          show: true,
          fields: ['subtotal'],
          formatter: (items) => {
            const total = items.reduce((acc, item) => {
              const q = parseFloat(item.quantity) || 0;
              const p = parseFloat(item.unit_price) || 0;
              item.subtotal = q * p;
              return acc + item.subtotal;
            }, 0);
            return { total };
          },
        },
      },
      {
        value: 'note',
        label: 'Notas',
        component: VTextarea,
      },
      {
        value: 'additional_data',
        label: 'Documentos',
        isJsonArray: true,
        plainJson: false,
        component: DynamicJsonField,
        col: 12,
        children: [
          {
            value: 'label',
            label: 'Etiqueta',
            component: VTextField,
            col: 6,
            required: false,
          },
          {
            value: 'file',
            label: 'Archivo',
            component: FileUploader,
            module: 'activities', // importante
            col: 6,
            required: false,
          }
        ],
      },
    ]
  },
  {
    title: 'Gastos',
    fields: [
      {
        value: 'expenses',
        label: 'Gastos relacionados',
        isJsonArray: true,
        component: DynamicJsonField,
        col: 12,
        children: [
          {
            value: 'concept',
            label: 'Concepto',
            component: VAutocomplete,
            searchValue: 'name',
            fetchOptions: 'expenseTypesFromStore',
            itemText: 'name',
            itemValue: 'id',
            fetchParams: () => ({
              'filter[name]': 'Conceptos de Gato'
            }),
            required: true,
            col: 6,
          },
          {
            value: 'amount',
            label: 'Monto',
            component: VTextField,
            type: 'number',
            prefix: '$',
            required: true,
            col: 6,
          },
          {
            value: 'paid_at',
            label: 'Fecha de pago',
            component: DatePickerInput,
            required: true,
            col: 6,
          },
          {
            value: 'user',
            label: 'Involucrado',
            component: VAutocomplete,
            searchValue: 'name',
            fetchOptions: 'searchUsers',
            itemText: (item: any) => [item?.name, item?.last_name].filter(Boolean).join(' ') || '',
            itemValue: (item: any) => item,
            fetchParams: () => ({
              'filter[is_active]': 1
            }),
            transform: (value: any) => ({
              user_id: value?.id ?? value
            }),
            required: true,
            col: 6,
          },
          
          {
            value: 'note',
            label: 'Nota',
            component: VTextarea,
            required: false,
            col: 12,
          },
        ],
        transform: (items: any[]) =>
          (items || []).map(item => ({
            concept_id: typeof item.concept === 'object' ? item.concept?.id : item.concept,
            amount: item.amount,
            paid_at: item.paid_at,
            user_id: typeof item.user === 'object' ? item.user?.id : item.user,
            note: item.note,
          }))
      }
    ]
  }
];

export const CREATE_FORM: FormField[] = CREATE_STEPS.flatMap(step => step.fields);

const idFieldMappings: Record<string, string> = {
  user_id: 'user',
  opportunity_id: 'opportunity',
  agency_id:'agency',
  account_id:'brand',
  contact_finance_id:'contact_finance',
  contact_campaign_id:'contact_campaign'
};

export const CONVERT_TO_SALE_FIELDS: FormField[] = [
  /*{
    value: 'additional_data.billing.organization',
    label: 'Empresa que Factura',
    component: VAutocomplete,
    fetchOptions: 'searchOrganizations',
    itemText: (item: any) => item.legal_name,
    itemValue: (item: any) => item,
    required: true,
    col: 6,
    transform: (value: any) => ({
      organization: value?.legal_name,
    }),
  },*/
  {
    value: 'organization',
    label: 'Empresa que Factura',
    component: VAutocomplete,
    options: [
      'GRUPO MASS COMUNICACIONES, SA DE CV',
      'POSTER PUBLICIDAD, SA DE CV',
      'DOMINIO RADIO, SA DE CV',
      'RADIO TELEVISORA DEL VALLE, SA DE CV',
      'MEGA ESTUDIOS, SA DE CV',
      'MEGA TELEVISORA, SA DE CV',
    ],
    itemText: (v: string) => v,
    itemValue: (v: string) => v,
    required: true,
    col: 6,
    transform: (value: string) => ({
      organization: value,
    }),
  },
  {
    value: 'additional_data.billing.invoice_date',
    label: 'Fecha de facturación',
    component: DatePickerInput,
    required: true,
    col: 6,
  },
  {
    value: 'additional_data.billing.note',
    label: 'Nota',
    component: VTextarea,
    required: false,
    col: 12,
    transform: (value: string) => ({ note: value ?? '' }),
  },
  {
    value: 'additional_data.pauta',
    label: 'Pauta',
    component: FileUploader,
    module: 'activities',
    col: 12,
    required: false,
  },
  {
    value: 'additional_data.orden_de_compra',
    label: 'Orden de Compra',
    component: FileUploader,
    module: 'activities',
    col: 12,
    required: false,
  },
];

export const EDIT_FORM: FormField[] = [...CREATE_FORM.map(field => {
  if (field.value === 'sale_items') {
    return {
      ...field,
      parse: (items: any[]) => items.map((item: any) => {
        const sold = item.sold_item || {};
        const isProduct = sold.type === 'products' || sold.productable_type === 'Product';
        const product_description = sold?.attributes?.description;
        return {
          quantity: item.quantity,
          unit_price: item.unit_price,
          productable_type: sold?.attributes?.productable_type ?? 'Product',
          productable_id: sold.id ?? null,
          item_id: isProduct ? sold.id : null,
          productables: isProduct ? [] : [sold],
          product_description: product_description ?? null
        };

      }),
    }
  }
  const relatedKey = idFieldMappings[field.value];
  if (relatedKey) {
    return {
      ...field,
      parse: (_: any, item: any) => item[relatedKey] || null,
      transform: (val: any) => ({
        [field.value]: val?.id ?? val
      })
    };
  }
  return field;
})].concat(CONVERT_TO_SALE_FIELDS);

export const HEADERS = [
  { title: '', value: 'eye_detail', format: 'text', sortable: false },
  { title: 'ID', value: 'id', format: 'text', sortable: true },
  { title: 'Cliente', value: 'client', format: 'text', sortable: false },
  { title: 'Campaña', value: 'campaign', format: 'text', sortable: true },
  { title: 'Subtotal', value: 'subtotal', format: 'currency', sortable: true },
  { title: 'IVA', value: 'iva', format: 'currency', sortable: true },
  { title: 'Total', value: 'total', format: 'currency', sortable: true },
  { title: 'Autorización', value: 'needs_authorization', format: 'text', sortable: true },
  { title: 'Vendedor', value: 'user', format: 'text', sortable: false },
  { title: 'Creación', value: 'created_at', format: 'text', sortable: true },
  { title: 'Programada?', value: 'additional_data.scheduled', sortable: false },
  { title: 'Factura', value: 'additional_data.billing.invoice', sortable: false },
  { title: 'Testigo', value: 'additional_data.evidence', sortable: false },
  ...(!IS_DOMINIOMEDIOS ? [
    { title: 'Departamento', value: 'influencer.department', format: 'text', sortable: true },
    { title: 'Influencer', value: 'influencer', format: 'text', sortable: true },
    { title: 'Status Campaña', value: 'campaing_status', format: 'text', sortable: true },
    { title: 'Contacto Campaña', value: 'campaign_contact_id', format: 'text', sortable: true },
    { title: 'Monto en Dolares', value: 'dollar_amount', format: 'text', sortable: true },
    { title: '% Agencia', value: 'peach_percentage', format: 'text', sortable: true },
    { title: '% Influencer', value: 'influencer_percentage', format: 'text', sortable: true },
    { title: '% Gastos', value: 'expenses_percetage', format: 'text', sortable: true },
    { title: '$ Agencia', value: 'peach_amount', format: 'text', sortable: true },
    { title: '$ Influencer', value: 'influencer_amount', format: 'text', sortable: true },
    { title: '$ Gastos', value: 'expenses_amount', format: 'text', sortable: true },
    { title: 'Contacto Financiero', value: 'finance_contact_id', format: 'text', sortable: true },
    { title: 'Status Cobranza', value: 'collection_status', format: 'text', sortable: true },
    { title: 'Factura', value: 'invoice.invoice', format: 'text', sortable: true },
    { title: 'Factura Adjunta', value: 'invoice.pdf', format: 'text', sortable: true },
    { title: 'Fecha Factura', value: 'invoice.date', format: 'text', sortable: true },
    { title: 'Vencimiento', value: 'expiration', format: 'text', sortable: true },
    { title: 'Vencida/En Tiempo', value: 'expired', format: 'text', sortable: true },
    { title: 'Comentarios Cobranza', value: 'collection_comment', format: 'text', sortable: true },
    { title: 'Factura Influencer', value: 'influencer_invoice', format: 'text', sortable: true },
    { title: 'Comprobante de Pago Influencer', value: 'influencer_payment_receipt', format: 'text', sortable: true },
    { title: 'Fecha Pago Influencer', value: 'influencer_payment_date', format: 'text', sortable: true },
  ] : []),
];

export const EXPAND_HEADERS = [
  { title: 'Producto', value: 'description', format: 'text' },
  { title: 'Organización', value: 'sold_item.attributes.organization.name', format: 'text' },
  { title: 'Cantidad', value: 'quantity', format: 'decimal' },
  { title: 'Precio unitario', value: 'unit_price', format: 'currency' },
  { title: 'Total', value: 'price_with_iva', format: 'currency' },
  { title: 'Autorización', value: 'needs_authorization', format: 'text' },
  { value: 'actions', sortable: false, align: 'end' },
];

export const DEFAULT_HEADER_ORDER = !IS_DOMINIOMEDIOS ? [
  'eye_detail',
  'id',
  'influencer.department',
  'influencer',
  '2',
  'client',
  'campaign',
  'campaign_contact_id',
  'subtotal',
  'iva',
  'total',
  'dollar_amount',
  'influencer_percentage',
  'peach_percentage',
  'expenses_percetage',
  'influencer_amount',
  'peach_amount',
  'expenses_amount',
  'needs_authorization',
  'user',
  'created_at',
  'finance_contact_id',
  'collection_status',
  'invoice.invoice',
  'invoice.pdf',
  'invoice.date',
  'expiration',
  'expired',
  'collection_comment',
  'influencer_invoice',
  'influencer_payment_receipt',
  'influencer_payment_date',
]:[
  'eye_detail',
  'id',
  'client',
  'campaign',
  'subtotal',
  'iva',
  'total',
  'needs_authorization',
  'user',
  'additional_data.scheduled',
  'additional_data.billing.invoice',
  'additional_data.evidence',
  'created_at',
];

export const DETAIL = {
  expand_table: true,
  eye_detail: true,
  key: 'sale_items',
  component: SaleDetail,
  custom_expand_detail:false
};

export const ROW_ACTIONS = [
  { label:'Editar',   onClick:'__edit',   permission:'sales.update',  icon:'mdi-pencil', kind:'default' },
  { label:'Eliminar', onClick:'__delete', permission:'sales.delete',  icon:'mdi-delete', kind:'default' },
  { label:'Autorizar',onClick:'authorizeSale', permission:'sales.authorize', icon:'mdi-check', kind:'extra', showIf: (item:any) => !!item?.needs_authorization },
];

export const EXPAND_ROW_ACTIONS = [
  { label:'Editar',   onClick:'__edit',   permission:'sales.update',  icon:'mdi-pencil', kind:'default' },
  { label:'Eliminar', onClick:'__delete', permission:'sales.delete',  icon:'mdi-delete', kind:'default' },
  //{ label:'Autorizar',onClick:'authorizeSale', permission:'sales.authorize', icon:'mdi-check', kind:'extra', showIf: (item:any) => !!item?.needs_authorization, },
];

export const FILTERS = [
  { value: 'id', label: 'ID', component: VTextField },
  { value: 'needs_authorization', label: 'Necesita Autorización', component: VSelect, options: [
      { label: 'Sí', value: true },
      { label: 'No', value: false },
    ],
    itemText: 'label',
    itemValue: 'value',
  },
  { value: 'client_id', 
    label: 'Cliente', 
    component: VAutocomplete, 
    fetchOptions: 'searchClientsAndLeads', 
    searchValue: 'name',
    itemText: (item: any) => item?.attributes?.commercial_name || item?.attributes?.legal_name || item?.attributes?.name || '', 
    itemValue: (item: any) => item,
    transform: (values: any) => ({
      client_id: Array.isArray(values)
      ?values.map(v=>v.id??v).join(',')
      :values?.id ?? values
    }),
    multiple:true
  },
  { value: 'user_id', label: 'Vendedor', component: VAutocomplete, fetchOptions: 'searchUsers', 
    searchValue: 'name',
    itemText: (item: any) => [item?.name, item?.last_name].filter(Boolean).join(' ') || '',
    itemValue: (item: any) => item,
    transform: (values: any) => ({
      user_id: Array.isArray(values)
      ?values.map(v=>v.id??v).join(',')
      :values?.id ?? values
    }),
    multiple:true
  },
  { value: 'created_between', label: 'Fecha de Creación', component: DatePickerInput, range: true },
  { value: 'invoice_date_between', label: 'Fecha de Facturación', component: DatePickerInput, range: true },
  //{ value: 'product_id', label: 'Producto', component: VAutocomplete, fetchOptions: 'searchProducts', searchValue: 'description', itemText: 'attributes.description', itemValue: 'id'},
  { value: 'note', label: 'Notas', component: VTextField },
  { value: 'has_invoice', label: 'Tiene factura?', component: VSelect, options: [
      { label: 'Sí', value: true },
      { label: 'No', value: false },
    ],
    itemText: 'label',
    itemValue: 'value',
  },
  { value: 'has_evidence', label: 'Tiene Testigo?', component: VSelect, options: [
      { label: 'Sí', value: true },
      { label: 'No', value: false },
    ],
    itemText: 'label',
    itemValue: 'value',
  },
  { value: 'is_scheduled', label: 'Esta Programado?', component: VSelect, options: [
      { label: 'Sí', value: true },
      { label: 'No', value: false },
    ],
    itemText: 'label',
    itemValue: 'value',
  },
];

export const ADMIN_FILTERS = FILTERS.map((filter) => (
  filter.value === 'has_invoice'
    ? { ...filter, defaultValue: false }
    : filter
));

export const ADMIN_HEADERS = [
  { title: 'ID', value: 'id', sortable: true },
  { title: 'Cliente', value: 'client', sortable: false },
  { title: 'Orden de Compra', value: 'additional_data.orden_de_compra', sortable: false },
  { title: 'Org. Factura', value: 'additional_data.billing.organization', sortable: false },
  { title: 'Fecha Facturación', value: 'additional_data.billing.invoice_date', sortable: true },
  { title: 'Nota', value: 'additional_data.billing.note', sortable: false },
  { title: 'Total', value: 'total', format: 'currency', sortable: true },
  { title: 'Factura Cargada', value: 'has_invoice', sortable: false },
  { title: 'Factura', value: 'additional_data.billing.invoice', sortable: false },
  { title: 'Orden de Facturación', value: 'invoice_order', sortable: false },
  { title: 'Creación', value: 'created_at', sortable: true },
];

export const ADMIN_DEFAULT_HEADER_ORDER = [
  'id','client','additional_data.orden_de_compra','additional_data.billing.organization','additional_data.billing.invoice_date','additional_data.billing.note','total','has_invoice','additional_data.billing.invoice','invoice_order','created_at'
];

export const CONTINUITY_HEADERS = [
  { title: 'ID', value: 'id', sortable: true },
  { title: 'Cliente', value: 'client', sortable: true },
  { title: 'Pauta', value: 'additional_data.pauta', sortable: false },
  { title: 'Programada?', value: 'additional_data.scheduled', sortable: false },
];

export const CONTINUITY_DEFAULT_HEADER_ORDER = ['id', 'client','additional_data.pauta', 'additional_data.scheduled'];

export const CANCELLATIONS_HEADERS = HEADERS

export const CANCELLATIONS_DEFAULT_HEADER_ORDER = DEFAULT_HEADER_ORDER

export const ADMIN_ROW_ACTIONS = [
  { label: 'Confirmar', onClick: 'confirmSale', permission:'sales.confirm', icon:'mdi-check', kind:'extra', showIf: (item:any) => !item?.additional_data?.confirmed }, 
  { label: 'Orden de Facturación', onClick: 'exportInvoiceOrder', icon:'mdi-download', kind:'extra', showIf: (item:any) => !item?.additional_data?.billing != undefined }, 
  { label:'Cargar Factura', onClick:'uploadFile', icon:'mdi-upload', kind:'extra', args: { target:'additional_data.billing.invoice', label:'Factura', module:'activities' } },
];

export function getView(route: any): 'admin'|'continuity'|undefined {
  const m = route.path.match(/^\/sales\/(admin|continuity)/i);
  return (m?.[1] as any) || undefined;
}

export const CONTINUITY_ROW_ACTIONS = [
  { label: 'Programado', onClick: 'scheduledSale', permission:'sales.program' },
  { label:'Cargar Testigo', onClick:'uploadFile', icon:'mdi-upload', kind:'extra', args: { target:'additional_data.evidence', label:'Testigo', module:'activities' } },
];

export const CANCELLATIONS_ROW_ACTIONS = [
  { label: 'Retomar venta', onClick: 'resumeSale', permission:'collections.update' },
  { label:'Eliminar', onClick:'__delete', permission:'collections.delete',  icon:'mdi-delete', kind:'default' },
];

export function getPreset(view?: 'admin'|'continuity'|'cancellations') {
  if (view === 'admin') {
    return {
      headers: ADMIN_HEADERS,
      defaultHeaderOrder: ADMIN_DEFAULT_HEADER_ORDER,
      filters: ADMIN_FILTERS,
      rowActions: ADMIN_ROW_ACTIONS,
      defaultQuery: { 'filter[has_billing]': 1, 'filter[has_invoice]': 0 },
    };
  }
  if (view === 'continuity') {
    return {
      headers: CONTINUITY_HEADERS,
      defaultHeaderOrder: CONTINUITY_DEFAULT_HEADER_ORDER,
      rowActions:CONTINUITY_ROW_ACTIONS,
      filters: FILTERS,
      defaultQuery: { 'filter[confirmed]': 1 },
    };
  }
  if (view === 'cancellations') {
    return {
      headers: CANCELLATIONS_HEADERS,
      defaultHeaderOrder: DEFAULT_HEADER_ORDER,
      filters: FILTERS,
      rowActions: CANCELLATIONS_ROW_ACTIONS,
      defaultQuery: { 'filter[status]': 'Cancelled' },
    };
  }
  return {
    headers: HEADERS,
    defaultHeaderOrder: DEFAULT_HEADER_ORDER,
    filters: FILTERS,
    rowActions: ROW_ACTIONS,
    defaultQuery: {},
  };
}

export const CARD_FIELDS: CardField[] = [
{ title: 'ID', value: 'id', section: 'header', format: 'chip', cols: 4, showLabel: true },
{ title: 'Cliente', value: 'client', section: 'header', format: 'text', cols: 8, showLabel: true },
{ title: 'Campaña', value: 'campaign', format: 'text', cols: 12, showLabel: true },
{ title: 'Subtotal', value: 'subtotal', format: 'currency', cols: 6, showLabel: true },
{ title: 'IVA', value: 'iva', format: 'currency', cols: 6, showLabel: true },
{ title: 'Total', value: 'total', format: 'currency', cols: 12, showLabel: true },
{ title: 'Vendedor', value: 'user', format: 'text', cols: 12, icon: 'mdi-account', showLabel: true },
{ title: 'Creación', value: 'created_at', format: 'date', cols: 12, icon: 'mdi-calendar', showLabel: true },
{ title: 'Autorización', value: 'needs_authorization', format: 'chip', cols: 12, showLabel: true },
{ title: 'Programada?', value: 'additional_data.scheduled', format: 'text', cols: 6, showLabel: true },
{ title: 'Factura', value: 'additional_data.billing.invoice', format: 'download', cols: 6, showLabel: true },
];

export const DEFAULT_CARD_ORDER = [
'id', 'client', 'campaign', 'total', 'subtotal', 'iva', 'user', 'created_at', 'needs_authorization',
'additional_data.pauta', 'additional_data.orden_de_compra', 'additional_data.scheduled', 'additional_data.billing.invoice'
];

export const RENDERERS = {
  user: 'userFullName',
  client: 'clientName',
  needs_authorization: { name: 'chipBoolean', props: { label:'Requiere Autorización', color:'orange' } },
  'additional_data.pauta': { name: 'downloadLink', props: { label: 'Pauta' } },
  'additional_data.orden_de_compra': { name: 'downloadLink', props: { label: 'Orden de Compra' } },
  'additional_data.scheduled': { name: 'boolean', props: { label: 'Programada' } },
  has_invoice: { name:'boolean', props:{ trueLabel:'Sí', falseLabel:'No', pendingLabel:'No' } },
  'additional_data.billing.invoice': { name: 'downloadLink', props: { label: 'Factura' } },
  'additional_data.evidence': { name: 'downloadLink', props: { label: 'Testigo' } },
  invoice_order: { name:'downloadLink', props:{ label:'Orden de Facturación' } },
};

// ---------- Cards (ADMIN) ----------
export const ADMIN_CARD_FIELDS: CardField[] = [
  { title: 'ID', value: 'id', section: 'header', format: 'chip', cols: 3, showLabel: true },
  { title: 'Cliente', value: 'client', section: 'header', format: 'text', cols: 9, showLabel: true },
  { title: 'Org. Factura', value: 'additional_data.billing.organization', format: 'text', cols: 12, showLabel: true },
  { title: 'Fecha Facturación', value: 'additional_data.billing.invoice_date', format: 'date', cols: 12, showLabel: true, icon:'mdi-calendar' },
  { title: 'Nota', value: 'additional_data.billing.note', format: 'text', cols: 12, showLabel: true },
  { title: 'Total', value: 'total', format: 'currency', cols: 12, showLabel: true },
  { title: 'Orden de Compra', value: 'additional_data.orden_de_compra', format: 'download', cols: 6, icon:'mdi-download', showLabel: true },
  { title: 'Orden de Facturación', value: 'invoice_order', sortable: false },
];

export const ADMIN_DEFAULT_CARD_ORDER = [
  'id','client','additional_data.billing.organization','additional_data.billing.invoice_date',
  'additional_data.billing.note','total','additional_data.orden_de_compra', 'invoice_order'
];

export const ADMIN_RENDERERS = {
  client: 'clientName',
  'additional_data.orden_de_compra': { name:'downloadLink', props:{ label:'Orden de Compra' } },
  has_invoice: { name:'boolean', props:{ trueLabel:'Sí', falseLabel:'No', pendingLabel:'No' } },
  'additional_data.billing.invoice': { name:'downloadLink', props:{ label:'Factura' } },
  invoice_order: { name:'downloadLink', props:{ label:'Orden de Facturación' } },
};


// ---------- Cards (CONTINUITY) ----------
export const CONTINUITY_CARD_FIELDS: CardField[] = [
  { title: 'ID', value: 'id', section: 'header', format: 'chip', cols: 4, showLabel: true },
  { title: 'Cliente', value: 'client', section: 'header', format: 'text', cols: 8, showLabel: true },
  { title: 'Pauta', value: 'additional_data.pauta', format: 'download', cols: 12, icon:'mdi-download', showLabel: true },
];

export const CONTINUITY_DEFAULT_CARD_ORDER = ['id','client','additional_data.pauta'];

export const CONTINUITY_RENDERERS = {
  client: 'clientName',
  'additional_data.pauta': { name:'downloadLink', props:{ label:'Pauta' } },
};


// ---------- Selector de preset para CARDS ----------
export function getCardPreset(view?: 'admin'|'continuity') {
  if (view === 'admin') {
    return {
      cardFields: ADMIN_CARD_FIELDS,
      defaultCardOrder: ADMIN_DEFAULT_CARD_ORDER,
      renderers: ADMIN_RENDERERS,
    };
  }
  if (view === 'continuity') {
    return {
      cardFields: CONTINUITY_CARD_FIELDS,
      defaultCardOrder: CONTINUITY_DEFAULT_CARD_ORDER,
      renderers: CONTINUITY_RENDERERS,
    };
  }
  return {
    cardFields: CARD_FIELDS,
    defaultCardOrder: DEFAULT_CARD_ORDER,
    renderers: RENDERERS,
  };
}


export const UPLOAD_FILE_FIELD: FormField[] = [
  {
    value: 'file',
    label: 'Archivo',
    component: FileUploader,
    module: 'sales', // importante
    col: 12,
    required: false,
  },
];
